import React, {
  createElement,
  forwardRef,
  ForwardRefExoticComponent,
  LegacyRef,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
} from 'react';
import classnames from 'classnames';

import InputWrapper, {
  IInputWrapperProps,
} from '~/components/atoms/FormControls/InputWrapper';
import ButtonClean from '~/components/ButtonClean';

import styles from './Input.module.scss';

export type TInputCreatorProps<InputType> = IInputWrapperProps &
  InputType & {
    id?: string;
    icon?: ReactElement;
    onIconClick?: () => void;
    inputWrapperClassName?: string;
    iconClassName?: string;
  };

export default function InputCreator<
  InputType extends TInputCreatorProps<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
  >,
  RefType,
>(
  component: keyof JSX.IntrinsicElements,
  creatorClassName = '',
): ForwardRefExoticComponent<
  PropsWithoutRef<InputType> & RefAttributes<RefType>
> {
  function InputTemplate(
    {
      error,
      icon,
      id,
      label,
      onIconClick,
      errorAction,
      className,
      inputWrapperClassName,
      iconClassName,
      onClickWrapper,
      ...inputProps
    }: InputType,
    ref: LegacyRef<RefType> | undefined,
  ): JSX.Element {
    const IconComponent = onIconClick ? ButtonClean : 'div';

    return (
      <InputWrapper
        label={label}
        errorAction={errorAction}
        error={error}
        id={id}
        className={classnames(creatorClassName, inputWrapperClassName)}
        onClickWrapper={onClickWrapper}
      >
        {createElement(component, {
          // TS detects proper ref type this way
          className: classnames(
            styles.input,
            { [styles.inputError]: !!error },
            className,
          ),
          id,
          ref,
          ...inputProps,
        })}
        {icon && (
          <IconComponent
            className={classnames(styles.icon, iconClassName)}
            onClick={onIconClick}
          >
            {icon}
          </IconComponent>
        )}
      </InputWrapper>
    );
  }

  return forwardRef<RefType, InputType>(InputTemplate);
}
